<script setup lang="ts">
	import { useHotelsStore } from '@/stores/hotels'
	import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
	import { nextTick, ref } from 'vue'
	import { useToast } from 'vue-toastification'

	import { useSearchPlaces } from '@/composables/useSearchPlaces'

	import { DestinationTypes } from '@/enums/destinationTypes'

	import type { Suggestion } from '@/interfaces/SearchPlace'

	import useHotels from '../composables/useHotels'
	import { usePlaceDetails } from '../composables/usePlaceDetails'

	// Quick-edit de destino: SOLO ciudad/región (Google Places), sin favoritos,
	// hoteles por nombre ni aeropuertos/POI — eso se queda en el form completo
	// (AvailabilityForm.vue). Esta versión es a propósito acotada, para cambiar
	// rápido de ciudad sin abrir el form grande.
	const {
		searchPlaceQuery,
		setSearchPlaceQuery,
		suggestionPlaces,
		isFetching: isSearching,
		setSelectedPlace,
	} = useSearchPlaces({
		params: {
			includedPrimaryTypes: [
				'locality',
				'administrative_area_level_1',
				'country',
			],
		},
	})

	const { retryDetails, isFetching: isResolving } = usePlaceDetails()
	// Solo guarda el destino en el form (borrador); no busca sola. El botón de
	// refrescar de AvailabilitySummarySearch es el único disparador real.
	const { setFormValues } = useHotels()
	const toast = useToast()

	const resolving = ref(false)
	const buttonEl = ref<{ $el?: HTMLElement } | null>(null)
	const panelStyle = ref<Record<string, string>>({})

	// El panel se teleporta a <body> (ver template) porque vive dentro de varios
	// contenedores `sticky` anidados con su propio stacking context: aunque el
	// panel tenga z-index alto, un hermano sticky posterior en el DOM lo tapaba
	// igual. Teleportado, se posiciona a mano contra el botón (position: fixed,
	// coordenadas de viewport — no hace falta sumar scroll).
	function updatePosition() {
		const el = buttonEl.value?.$el
		if (!el) return
		const rect = el.getBoundingClientRect()
		const width = Math.min(288, window.innerWidth - 24)
		panelStyle.value = {
			position: 'fixed',
			top: `${Math.min(rect.bottom + 8, Math.max(12, window.innerHeight - 320))}px`,
			left: `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`,
			width: `${width}px`,
			maxHeight: `${Math.max(100, window.innerHeight - rect.bottom - 20)}px`,
			overflowY: 'auto',
		}
	}

	async function selectPlace(suggestion: Suggestion, close: () => void) {
		resolving.value = true
		setSelectedPlace(suggestion.placePrediction)

		const resp = await retryDetails()

		if (resp?.data) {
			setFormValues({
				companyBranchId: undefined,
				distance:
					useHotelsStore().searchContext?.radiusSource === 'custom' ?
						useHotelsStore().form.distance
					:	undefined,
				latitude: resp.data.location.latitude,
				longitude: resp.data.location.longitude,
				cityName: resp.data.displayName.text,
				destinationCategory: resp.data.types?.[0] as DestinationTypes,
				// Sin esto el historial guardaría el destino anterior con las coordenadas nuevas.
				placeId: suggestion.placePrediction.placeId,
				destinationLabel: suggestion.placePrediction.text?.text,
			})
			toast.info('Destino actualizado. Pica "Actualizar" para buscar.', {
				timeout: 3000,
			})
		} else {
			toast.error('No se pudo resolver ese destino, intenta con otra búsqueda.')
		}

		setSearchPlaceQuery('')
		resolving.value = false
		close()
	}

	function onInput(value: string) {
		setSearchPlaceQuery(value)
	}
</script>

<template>
	<Popover
		v-slot="{ open, close }"
		class="relative"
	>
		<PopoverButton
			ref="buttonEl"
			class="flex items-center gap-1.5 sm:gap-2 bg-white rounded-lg border border-gray-200 px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-sm hover:border-[#021F58]/40 transition-colors outline-none"
			@click="nextTick(updatePosition)"
		>
			<span
				class="material-symbols-outlined text-[#021F58] text-sm sm:text-base"
				>location_on</span
			>
			<div class="flex flex-col leading-tight text-left">
				<span
					class="text-[9px] sm:text-[10px] text-gray-400 font-medium uppercase tracking-wide"
					>Destino</span
				>
				<span
					class="text-[11px] sm:text-sm font-semibold text-gray-900 max-w-[90px] sm:max-w-none truncate"
				>
					<slot name="value" />
				</span>
			</div>
			<span class="material-symbols-outlined text-gray-400 text-sm">edit</span>
		</PopoverButton>

		<Teleport to="body">
			<PopoverPanel
				v-if="open"
				:style="panelStyle"
				class="z-[9999] w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
			>
				<label class="block text-xs font-medium text-gray-500 mb-1.5"
					>Buscar nueva ciudad</label
				>
				<input
					:value="searchPlaceQuery"
					type="text"
					placeholder="Ej. Madrid, España"
					class="w-full rounded-lg border-0 px-3 py-2 text-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-[#021F58]/30"
					@input="onInput(($event.target as HTMLInputElement).value)"
				/>

				<div class="mt-2 max-h-56 overflow-y-auto">
					<div
						v-if="isSearching"
						class="py-3 text-center text-xs text-gray-400"
					>
						Buscando...
					</div>
					<div
						v-else-if="searchPlaceQuery && suggestionPlaces?.length === 0"
						class="py-3 text-center text-xs text-gray-400"
					>
						Sin resultados.
					</div>
					<button
						v-for="suggestion in suggestionPlaces"
						:key="suggestion.placePrediction.placeId"
						type="button"
						:disabled="resolving || isResolving"
						class="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-50"
						@click="selectPlace(suggestion, close)"
					>
						<span class="material-symbols-outlined text-gray-400 text-base"
							>place</span
						>
						<span class="truncate">{{
							suggestion.placePrediction.text?.text
						}}</span>
					</button>
				</div>
			</PopoverPanel>
		</Teleport>
	</Popover>
</template>
